import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from 'axios';

import { getFirebaseBackend } from 'helpers/firebaseHelper';
import { useAuth } from 'hooks/useAuth';
import PopoverBulkTags from '../Popover-Bulk-Tags';
import PopoverBulkMove from '../Popover-Bulk-Move';
import ModalBulkDeleteKeywords from '../Modal-Bulk-Delete-Keywords';
import { BulkActionsBarContainer, BulkActionsBarStyled } from './styles';
import useBarPosition from './useBarPosition';

const SUCCESS_TOAST_OPTIONS = { theme: 'colored', style: { backgroundColor: '#38a169' } };

const ToastContent = ({ title, description }) => (
  <div>
    <strong className="d-block">{title}</strong>
    {description && <span>{description}</span>}
  </div>
);

ToastContent.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
};

const BulkActionsBar = ({
  selectedIds,
  allKeywords,
  groups,
  accountTags,
  onSelectAll,
  onClearSelection,
  onActionDone,
}) => {
  const { t: translate } = useTranslation();
  const firebaseHelper = getFirebaseBackend();
  const { user } = useAuth();
  const { id: groupId } = useParams();
  const accountId = user.account;
  const containerRef = useRef();
  const position = useBarPosition(containerRef);
  const tagsRef = useRef();
  const moveRef = useRef();
  const [openPopover, setOpenPopover] = useState(null);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const selectedCount = selectedIds.length;
  const totalCount = allKeywords.length;
  const isAllSelected = selectedCount === totalCount;
  const selectedKeywords = allKeywords.filter((keyword) => selectedIds.includes(keyword.id));

  useEffect(() => {
    if (!openPopover) return;

    const popoverRef = openPopover === 'tags' ? tagsRef : moveRef;

    const handleMouseDown = (event) => {
      if (!isSending && !popoverRef.current?.contains(event.target)) setOpenPopover(null);
    };
    const handleKeyDown = (event) => {
      if (!isSending && event.key === 'Escape') setOpenPopover(null);
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openPopover, isSending]);

  const togglePopover = (popover) => {
    setOpenPopover((prevPopover) => (prevPopover === popover ? null : popover));
  };

  const applyTags = async (tagsToAdd, tagsToRemove) => {
    try {
      setIsSending(true);
      const updatedCount = await firebaseHelper.updateKeywordsTags(
        accountId,
        groupId,
        selectedIds,
        tagsToAdd,
        tagsToRemove,
      );

      toast.success(
        <ToastContent
          title={
            updatedCount === 1
              ? translate('Tags updated on 1 keyword')
              : translate('Tags updated on {{count}} keywords', { count: updatedCount })
          }
        />,
        SUCCESS_TOAST_OPTIONS,
      );
      setOpenPopover(null);
      onActionDone({ clearSelection: false, refreshTags: true });
    } catch (error) {
      console.error('Error updating keywords tags: ', error);
      const updatedCount = error?.updatedCount ?? 0;

      toast.error(
        updatedCount > 0
          ? translate(
              'Tags were updated on {{count}} keywords before an error. Try again to update the rest.',
              { count: updatedCount },
            )
          : translate('Error updating tags'),
      );
      setOpenPopover(null);
      onActionDone({ clearSelection: false, refreshTags: true });
    } finally {
      setIsSending(false);
    }
  };

  const moveKeywords = async (newGroup) => {
    try {
      setIsSending(true);
      const movedCount = await firebaseHelper.moveKeywordsToNewGroup(
        accountId,
        groupId,
        newGroup.id,
        selectedIds,
      );

      toast.success(
        <ToastContent
          title={
            movedCount === 1
              ? translate('1 keyword moved')
              : translate('{{count}} keywords moved', { count: movedCount })
          }
          description={translate('Destination: {{group}}', {
            group: newGroup.name === 'Desagrupado' ? translate('Not grouped') : newGroup.name,
          })}
        />,
        SUCCESS_TOAST_OPTIONS,
      );
      setOpenPopover(null);
      onActionDone({ clearSelection: true, refreshGroups: true });
    } catch (error) {
      console.error('Error moving keywords: ', error);
      toast.error(translate('Error moving keywords'));
    } finally {
      setIsSending(false);
    }
  };

  const deleteKeywords = async () => {
    try {
      setIsSending(true);
      const firebaseToken = await firebaseHelper.getIdToken();

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/deleteKeywords`,
        {
          development: process.env.REACT_APP_BUILD_TYPE !== 'production',
          accountId,
          groupId,
          keywordIds: selectedIds,
        },
        {
          headers: {
            Authorization: `Bearer ${firebaseToken}`,
          },
        },
      );

      toast.success(
        <ToastContent
          title={
            data.deleted === 1
              ? translate('1 keyword deleted')
              : translate('{{count}} keywords deleted', { count: data.deleted })
          }
        />,
        SUCCESS_TOAST_OPTIONS,
      );
      setIsOpenDeleteModal(false);
      onActionDone({ clearSelection: true, refreshGroups: true, refreshLimit: true });
    } catch (error) {
      console.error('Error deleting keywords: ', error);
      const deletedCount = error.response?.data?.deleted ?? 0;

      // The backend deletes in batches: some keywords may be gone even when the request fails
      toast.error(
        deletedCount > 0
          ? translate(
              '{{count}} keywords were deleted before an error. Try again to delete the rest.',
              { count: deletedCount },
            )
          : translate('Error deleting keywords'),
      );
      setIsOpenDeleteModal(false);
      onActionDone({ clearSelection: false, refreshGroups: true, refreshLimit: true });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <BulkActionsBarContainer ref={containerRef} style={position ?? { visibility: 'hidden' }}>
      <BulkActionsBarStyled data-figma-id="15934:7740">
        <div className="bulk-counter" data-figma-id="I15934:7740;15934:7715">
          <span>
            {selectedCount === 1
              ? translate('1 selected')
              : translate('{{count}} selected', { count: selectedCount })}
          </span>
          <button
            type="button"
            onClick={onClearSelection}
            disabled={isSending}
            aria-label={translate('Clear selection')}
            title={translate('Clear selection')}
          >
            <i className="bx bx-x" />
          </button>
        </div>
        {isAllSelected ? (
          <span className="bulk-all-selected" data-figma-id="I15934:7766;15934:7719">
            {translate('All from the group, on every page')}
          </span>
        ) : (
          <button
            type="button"
            className="bulk-select-all"
            data-figma-id="I15934:7740;15934:7719"
            onClick={onSelectAll}
            disabled={isSending}
          >
            {translate('Select all ({{count}})', { count: totalCount })}
          </button>
        )}
        <div className="bulk-divider" />
        <div className="bulk-action-wrapper" ref={tagsRef}>
          <button
            type="button"
            className={`bulk-action bulk-action-tags ${openPopover === 'tags' ? 'active' : ''}`}
            data-figma-id="I15934:7740;15934:7721"
            onClick={() => togglePopover('tags')}
            disabled={isSending}
          >
            <i className="bx bx-purchase-tag-alt" />
            {'Tags'}
          </button>
          {openPopover === 'tags' && (
            <PopoverBulkTags
              // Remounts when the selection changes, discarding the draft built for the old one
              key={selectedIds.join(',')}
              selectedKeywords={selectedKeywords}
              accountTags={accountTags}
              isSending={isSending}
              onApply={applyTags}
              onCancel={() => setOpenPopover(null)}
            />
          )}
        </div>
        <div className="bulk-action-wrapper" ref={moveRef}>
          <button
            type="button"
            className={`bulk-action bulk-action-move ${openPopover === 'move' ? 'active' : ''}`}
            data-figma-id="I15934:7740;15934:7726"
            onClick={() => togglePopover('move')}
            disabled={isSending}
          >
            <i className="bx bx-folder" />
            {translate('Move')}
          </button>
          {openPopover === 'move' && (
            <PopoverBulkMove
              selectedCount={selectedCount}
              groups={groups}
              currentGroupId={groupId}
              isSending={isSending}
              onMove={moveKeywords}
            />
          )}
        </div>
        <button
          type="button"
          className="bulk-action bulk-action-delete"
          data-figma-id="I15934:7740;15934:7730"
          onClick={() => {
            setOpenPopover(null);
            setIsOpenDeleteModal(true);
          }}
          disabled={isSending}
        >
          <i className="bx bx-trash" />
          {translate('Delete selected')}
        </button>
        <ModalBulkDeleteKeywords
          isOpen={isOpenDeleteModal}
          toggle={() => setIsOpenDeleteModal(false)}
          selectedCount={selectedCount}
          isSending={isSending}
          onDelete={deleteKeywords}
        />
      </BulkActionsBarStyled>
    </BulkActionsBarContainer>
  );
};

BulkActionsBar.propTypes = {
  selectedIds: PropTypes.array,
  allKeywords: PropTypes.array,
  groups: PropTypes.array,
  accountTags: PropTypes.array,
  onSelectAll: PropTypes.func,
  onClearSelection: PropTypes.func,
  onActionDone: PropTypes.func,
};

export default BulkActionsBar;
